import React, { useState } from 'react';
import { X, Mail, ChevronDown, ChevronUp, Loader2, Send, Edit2, Check } from 'lucide-react';

/**
 * BulkPreviewModal
 * Shows GPT-drafted emails per candidate before HR approves & sends.
 *
 * Props:
 *   drafts      — [{ resumeId, candidateName, email, subject, htmlBody, inviteLink }]
 *   stage       — { label }
 *   job         — { title }
 *   onApprove   — (approvedDrafts) => void   called when HR clicks "Approve & Send"
 *   onClose     — () => void
 *   sending     — bool
 */
const BulkPreviewModal = ({ drafts: initialDrafts, stage, job, onApprove, onClose, sending }) => {
  const [drafts, setDrafts]       = useState(initialDrafts);
  const [expanded, setExpanded]   = useState(null);  // resumeId of open card
  const [editing, setEditing]     = useState({});    // { [resumeId]: bool }

  const update = (resumeId, field, value) => {
    setDrafts(prev => prev.map(d => d.resumeId === String(resumeId) ? { ...d, [field]: value } : d));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-bold text-slate-800">Review Invite Emails</h2>
            <p className="text-xs text-slate-400 mt-0.5">
              {drafts.length} email{drafts.length !== 1 ? 's' : ''} drafted for <strong>{stage?.label}</strong> · {job?.title}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Draft list */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-3">
          {drafts.map((draft) => {
            const isOpen    = expanded === draft.resumeId;
            const isEditing = editing[draft.resumeId];

            return (
              <div key={draft.resumeId} className="border border-slate-200 rounded-xl overflow-hidden">
                {/* Card header */}
                <button
                  className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                  onClick={() => setExpanded(isOpen ? null : draft.resumeId)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-meraki-indigo/10 flex items-center justify-center text-meraki-indigo font-bold text-sm">
                      {(draft.candidateName?.[0] || '?').toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-700">{draft.candidateName}</p>
                      <p className="text-xs text-slate-400">{draft.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 truncate max-w-[200px] hidden sm:block">{draft.subject}</span>
                    {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>
                </button>

                {/* Expanded content */}
                {isOpen && (
                  <div className="px-4 py-4 border-t border-slate-100 space-y-3">
                    {/* Subject */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Subject</label>
                        <button
                          onClick={() => setEditing(e => ({ ...e, [draft.resumeId]: !isEditing }))}
                          className="flex items-center gap-1 text-xs text-meraki-indigo hover:underline"
                        >
                          {isEditing ? <><Check className="w-3 h-3" /> Done</> : <><Edit2 className="w-3 h-3" /> Edit</>}
                        </button>
                      </div>
                      {isEditing ? (
                        <input
                          type="text"
                          value={draft.subject}
                          onChange={e => update(draft.resumeId, 'subject', e.target.value)}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-meraki-indigo/30"
                        />
                      ) : (
                        <p className="text-sm text-slate-700 bg-slate-50 px-3 py-2 rounded-lg">{draft.subject}</p>
                      )}
                    </div>

                    {/* Body */}
                    <div>
                      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1">Email Body</label>
                      {isEditing ? (
                        <textarea
                          value={draft.htmlBody}
                          onChange={e => update(draft.resumeId, 'htmlBody', e.target.value)}
                          rows={8}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-meraki-indigo/30"
                        />
                      ) : (
                        <div
                          className="text-sm text-slate-700 bg-slate-50 px-3 py-3 rounded-lg max-h-52 overflow-y-auto prose prose-sm"
                          dangerouslySetInnerHTML={{ __html: draft.htmlBody }}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
          <p className="text-xs text-slate-400">
            You can expand each card to edit subject &amp; body before sending.
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={sending}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 border border-slate-200 rounded-lg hover:bg-white transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => onApprove(drafts)}
              disabled={sending}
              className="flex items-center gap-2 px-5 py-2 bg-meraki-indigo text-white rounded-lg text-sm font-bold hover:bg-indigo-800 transition-colors disabled:opacity-60"
            >
              {sending
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                : <><Send className="w-4 h-4" /> Approve &amp; Send All</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkPreviewModal;
